import { useEffect, useRef } from 'react';

interface Particle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    size: number;
    opacity: number;
    hue: number;
    life: number;
    maxLife: number;
    type: 'sparkle' | 'heart' | 'bubble' | 'glow';
}

const FloatingParticles = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const particlesRef = useRef<Particle[]>([]);
    const mouseRef = useRef({ x: 0, y: 0, moved: false });
    const animationFrameRef = useRef<number>();

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size
        const resizeCanvas = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resizeCanvas();
        window.addEventListener('resize', resizeCanvas);

        // Mouse tracking with elegant trail
        const handleMouseMove = (e: MouseEvent) => {
            mouseRef.current = { x: e.clientX, y: e.clientY, moved: true };
            
            // Create particles on mouse movement
            if (Math.random() > 0.7) {
                createParticle(e.clientX, e.clientY, 'sparkle');
            }
            
            setTimeout(() => {
                mouseRef.current.moved = false;
            }, 100);
        };
        window.addEventListener('mousemove', handleMouseMove);

        // Create a new particle
        const createParticle = (x: number, y: number, type: Particle['type']) => {
            const particle: Particle = {
                x,
                y,
                vx: (Math.random() - 0.5) * 2,
                vy: (Math.random() - 0.5) * 2 - 1, // Slight upward bias
                size: Math.random() * 4 + 2,
                opacity: 1,
                hue: type === 'heart' ? 340 + Math.random() * 20 : // Pink/red for hearts
                     type === 'bubble' ? 180 + Math.random() * 60 : // Blue/cyan for bubbles
                     type === 'glow' ? 45 + Math.random() * 30 : // Gold for glows
                     Math.random() * 360, // Random for sparkles
                life: 0,
                maxLife: Math.random() * 120 + 60,
                type
            };
            particlesRef.current.push(particle);
        };

        // Create ambient particles periodically
        const createAmbientParticles = () => {
            const types: Particle['type'][] = ['sparkle', 'heart', 'bubble', 'glow'];
            const type = types[Math.floor(Math.random() * types.length)];
            const x = Math.random() * canvas.width;
            const y = canvas.height + 20; // Start below screen
            createParticle(x, y, type);
        };

        // Ambient particle creation interval
        const ambientInterval = setInterval(() => {
            if (particlesRef.current.length < 100) {
                createAmbientParticles();
            }
        }, 200);

        // Animation loop
        const animate = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // Create gradient background overlay (very subtle)
            const gradient = ctx.createRadialGradient(
                canvas.width / 2, canvas.height / 2, 0,
                canvas.width / 2, canvas.height / 2, canvas.width
            );
            gradient.addColorStop(0, 'rgba(255, 240, 245, 0.02)');
            gradient.addColorStop(1, 'rgba(250, 235, 215, 0.01)');
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Update and draw particles
            particlesRef.current = particlesRef.current.filter(particle => {
                particle.life++;
                particle.x += particle.vx;
                particle.y += particle.vy;
                
                // Apply gentle gravity
                particle.vy -= 0.05;
                
                // Fade out as life ends
                particle.opacity = 1 - (particle.life / particle.maxLife);
                
                // Draw particle based on type
                ctx.save();
                ctx.globalAlpha = particle.opacity;
                
                switch (particle.type) {
                    case 'sparkle':
                        // Draw sparkle as a star
                        ctx.fillStyle = `hsl(${particle.hue}, 80%, 70%)`;
                        ctx.shadowBlur = 20;
                        ctx.shadowColor = `hsl(${particle.hue}, 100%, 80%)`;
                        drawStar(ctx, particle.x, particle.y, 5, particle.size, particle.size / 2);
                        break;
                    
                    case 'heart':
                        // Draw heart shape
                        ctx.fillStyle = `hsl(${particle.hue}, 85%, 65%)`;
                        ctx.shadowBlur = 15;
                        ctx.shadowColor = `hsl(${particle.hue}, 100%, 75%)`;
                        drawHeart(ctx, particle.x, particle.y, particle.size);
                        break;
                    
                    case 'bubble':
                        // Draw bubble with gradient
                        const bubbleGradient = ctx.createRadialGradient(
                            particle.x - particle.size / 3, particle.y - particle.size / 3, 0,
                            particle.x, particle.y, particle.size
                        );
                        bubbleGradient.addColorStop(0, `hsla(${particle.hue}, 70%, 80%, 0.8)`);
                        bubbleGradient.addColorStop(0.7, `hsla(${particle.hue}, 60%, 70%, 0.4)`);
                        bubbleGradient.addColorStop(1, `hsla(${particle.hue}, 50%, 60%, 0.1)`);
                        ctx.fillStyle = bubbleGradient;
                        ctx.shadowBlur = 10;
                        ctx.shadowColor = `hsla(${particle.hue}, 70%, 80%, 0.5)`;
                        ctx.beginPath();
                        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
                        ctx.fill();
                        break;
                    
                    case 'glow':
                        // Draw soft glow orb
                        const glowGradient = ctx.createRadialGradient(
                            particle.x, particle.y, 0,
                            particle.x, particle.y, particle.size * 2
                        );
                        glowGradient.addColorStop(0, `hsla(${particle.hue}, 100%, 75%, 0.6)`);
                        glowGradient.addColorStop(0.5, `hsla(${particle.hue}, 90%, 70%, 0.3)`);
                        glowGradient.addColorStop(1, `hsla(${particle.hue}, 80%, 65%, 0)`);
                        ctx.fillStyle = glowGradient;
                        ctx.beginPath();
                        ctx.arc(particle.x, particle.y, particle.size * 2, 0, Math.PI * 2);
                        ctx.fill();
                        break;
                }
                
                ctx.restore();
                
                // Remove if life is over or out of bounds
                return particle.life < particle.maxLife && 
                       particle.y > -50 && 
                       particle.x > -50 && 
                       particle.x < canvas.width + 50;
            });

            animationFrameRef.current = requestAnimationFrame(animate);
        };

        animate();

        // Cleanup
        return () => {
            window.removeEventListener('resize', resizeCanvas);
            window.removeEventListener('mousemove', handleMouseMove);
            clearInterval(ambientInterval);
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 pointer-events-none"
            style={{ zIndex: 1 }}
        />
    );
};

// Helper function to draw a star
const drawStar = (
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    spikes: number,
    outerRadius: number,
    innerRadius: number
) => {
    let rot = (Math.PI / 2) * 3;
    let x = cx;
    let y = cy;
    const step = Math.PI / spikes;

    ctx.beginPath();
    ctx.moveTo(cx, cy - outerRadius);

    for (let i = 0; i < spikes; i++) {
        x = cx + Math.cos(rot) * outerRadius;
        y = cy + Math.sin(rot) * outerRadius;
        ctx.lineTo(x, y);
        rot += step;

        x = cx + Math.cos(rot) * innerRadius;
        y = cy + Math.sin(rot) * innerRadius;
        ctx.lineTo(x, y);
        rot += step;
    }

    ctx.lineTo(cx, cy - outerRadius);
    ctx.closePath();
    ctx.fill();
};

// Helper function to draw a heart
const drawHeart = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
    ctx.beginPath();
    const topCurveHeight = size * 0.3;
    ctx.moveTo(x, y + topCurveHeight);
    // Top left curve
    ctx.bezierCurveTo(
        x, y,
        x - size / 2, y,
        x - size / 2, y + topCurveHeight
    );
    // Bottom left curve
    ctx.bezierCurveTo(
        x - size / 2, y + (size + topCurveHeight) / 2,
        x, y + (size + topCurveHeight) / 1.2,
        x, y + size
    );
    // Bottom right curve
    ctx.bezierCurveTo(
        x, y + (size + topCurveHeight) / 1.2,
        x + size / 2, y + (size + topCurveHeight) / 2,
        x + size / 2, y + topCurveHeight
    );
    // Top right curve
    ctx.bezierCurveTo(
        x + size / 2, y,
        x, y,
        x, y + topCurveHeight
    );
    ctx.closePath();
    ctx.fill();
};

export default FloatingParticles;
